import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import {
  downloadHrDailyReportPdf,
  getHrDailyDrilldown,
  getHrDailyReport
} from '../../../../services/api/implementation/impl';
import DownloadPdfButton from '../../../Engineer/reports/DownloadPdfButton';
import useReportPdf from '../shared/useReportPdf';
import ReportShell, { CardGrid, FilterSelect, ResultBanner } from '../shared/ReportShell';
import DateRangeControl from '../shared/DateRangeControl';
import DrilldownDrawer from '../shared/DrilldownDrawer';
import MetricCard from '../shared/MetricCard';
import SectionTitle from '../shared/SectionTitle';
import useDailyReport from '../shared/useDailyReport';
import { DEFAULT_RANGE, PRESET, rangeLabel, rangeParams } from '../shared/dateRange';
import { fmtPct } from '../shared/format';
import EmployeeTable from './EmployeeTable';

/** "Department" is the user's vertical — the system has no department list. */
const DEPARTMENTS = ['Sales', 'Operations', 'Accounts', 'Management', 'Marketing', 'Inventory', 'Legal', 'HR', 'Staff'];

/** HR walks every person-day, so its custom range is capped (the API refuses more). */
const HR_MAX_DAYS = 93;

const STATUS_LABEL = {
  logged_in: 'Logged in',
  on_leave: 'On leave',
  absent: 'Absent',
  not_logged_in: 'Not logged in'
};

function describeRecord(item) {
  if (item.type === 'followUp' || item.type === 'task') {
    return {
      key: `${item.type}-${item.id}`,
      title: item.title || (item.type === 'task' ? 'Task' : 'Follow-up'),
      subtitle: [item.assignee, item.status].filter(Boolean).join(' · '),
      meta: item.date ? moment(item.date).format('D MMM') : '',
      href:
        item.type === 'task'
          ? `/builder/management/tasks/${item.id}`
          : item.leadId
            ? `/builder/sales/${item.leadId}`
            : null
    };
  }
  return {
    key: `${item.userId}-${item.date}`,
    title: item.name,
    subtitle: [item.vertical, STATUS_LABEL[item.status]].filter(Boolean).join(' · '),
    meta: item.checkIn
      ? `In ${moment(item.checkIn).format('h:mm A')}, ${moment(item.date).format('D MMM')}`
      : moment(item.date).format('D MMM'),
    href: null
  };
}

const HrDailyReport = () => {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [vertical, setVertical] = useState('');
  const [drill, setDrill] = useState(null);

  const params = useMemo(() => ({ ...rangeParams(range), vertical }), [range, vertical]);
  const { data, loading, error, refetch } = useDailyReport(getHrDailyReport, params);
  const { downloadPdf, isDownloading } = useReportPdf(downloadHrDailyReportPdf);

  const fetchPage = useCallback(
    (page) => getHrDailyDrilldown({ ...params, metric: drill?.metric, ...page }),
    [params, drill]
  );

  const team = data?.team;
  const label = data ? rangeLabel(data.range) : '';
  // For today the day is not over, so "not logged in" still means "not yet".
  const isToday = range.preset === PRESET.TODAY;
  const notInLabel = isToday ? 'Not yet logged in' : 'Not logged in';
  const singleDay = data?.range && data.range.from === data.range.to;

  const open = (metric, title, subtitle) => setDrill({ metric, title, subtitle });

  const banner = team
    ? [
        {
          key: 'in',
          tone: team.loggedInPct === null || team.loggedInPct >= 90 ? 'good' : team.loggedInPct >= 75 ? 'warn' : 'bad',
          text: (
            <>
              <strong>{fmtPct(team.loggedInPct)} logged in</strong>: {team.loggedIn} of {team.expected} expected to
              work.
            </>
          )
        },
        {
          key: 'out',
          tone: team.absent + team.notYetLoggedIn > 0 ? 'warn' : 'good',
          text: (
            <>
              <strong>
                {team.absent} absent, {team.notYetLoggedIn} {notInLabel.toLowerCase()}
              </strong>
              ; {team.onLeave} on approved leave.
            </>
          )
        },
        {
          key: 'work',
          tone: (team.tasks.pct ?? 100) >= 80 && (team.calls.pct ?? 100) >= 80 ? 'good' : 'warn',
          text: (
            <>
              <strong>{fmtPct(team.tasks.pct)} of tasks done</strong> ({team.tasks.completed} of {team.tasks.due}); sales
              calls {fmtPct(team.calls.pct)} ({team.calls.completed} of {team.calls.assigned}).
            </>
          )
        }
      ]
    : [];

  return (
    <ReportShell
      title="HR Daily Report"
      subtitle={label ? `Report date: ${label}` : 'Attendance, leave, calls and tasks'}
      loading={loading}
      hasData={!!data}
      error={error}
      onRetry={refetch}
      actions={
        <DownloadPdfButton
          onClick={() => downloadPdf(params)}
          isDownloading={isDownloading}
          disabled={!data || loading || !!error}
        />
      }
      filters={
        <>
          <FilterSelect label="Department" value={vertical} onChange={setVertical}>
            <option value="">All departments</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </FilterSelect>
          <DateRangeControl value={range} onChange={setRange} allowLifetime={false} maxDays={HR_MAX_DAYS} />
        </>
      }
    >
      {team && (
        <>
          <ResultBanner title={`Result for ${label}`} items={banner} />

          <section aria-labelledby="attendance" className="flex flex-col gap-4">
            <SectionTitle
              id="attendance"
              note={`${team.expected} expected to work${singleDay ? '' : ' (person-days)'} · week-offs and holidays excluded`}
            >
              Attendance
            </SectionTitle>
            <CardGrid min={220}>
              <MetricCard
                label="Employees logged in"
                headline={fmtPct(team.loggedInPct)}
                unit="of expected"
                detail={`${team.loggedIn} of ${team.expected} checked in`}
                percent={team.loggedInPct}
                onClick={() => open('loggedIn', 'Logged in', 'Checked in, or marked present / WFH / on duty.')}
              />
              <MetricCard
                label="On approved leave"
                headline={team.onLeave}
                unit="people"
                detail="Leave approved"
                onClick={() => open('onLeave', 'On approved leave', 'Leave approved for the day.')}
              />
              <MetricCard
                label="Absent"
                headline={team.absent}
                unit="people"
                detail="Marked absent"
                alert={team.absent > 0}
                onClick={() => open('absent', 'Absent', 'Marked absent for the day.')}
              />
              <MetricCard
                label={notInLabel}
                headline={team.notYetLoggedIn}
                unit="people"
                detail={isToday ? 'No check-in yet today' : 'No check-in, no leave, not marked absent'}
                alert={!isToday && team.notYetLoggedIn > 0}
                onClick={() => open('notYetLoggedIn', notInLabel, 'Expected to work, with no check-in and no leave.')}
              />
            </CardGrid>
          </section>

          <section aria-labelledby="work" className="flex flex-col gap-4">
            <SectionTitle id="work" tone="teal" note="Completed against what was due in this period">
              Work
            </SectionTitle>
            <CardGrid min={260}>
              <MetricCard
                label="Sales calls completed"
                headline={fmtPct(team.calls.pct)}
                unit="completion"
                detail={`${team.calls.completed} of ${team.calls.assigned} assigned calls`}
                percent={team.calls.pct}
                onClick={() => open('callsPending', 'Calls not completed', 'Follow-up calls due and not done.')}
              />
              <MetricCard
                label="Tasks completed"
                headline={fmtPct(team.tasks.pct)}
                unit="completion"
                detail={`${team.tasks.completed} of ${team.tasks.due} tasks due`}
                percent={team.tasks.pct}
                onClick={() => open('tasksPending', 'Tasks not completed', 'Due in this period and not completed yet.')}
              />
            </CardGrid>
          </section>

          {(data.perEmployee || []).length > 0 && (
            <section aria-labelledby="people" className="flex flex-col gap-4">
              <SectionTitle id="people">By Employee</SectionTitle>
              <EmployeeTable rows={data.perEmployee} singleDay={singleDay} notInLabel={notInLabel} />
            </section>
          )}
        </>
      )}

      <DrilldownDrawer
        open={!!drill}
        title={drill?.title || ''}
        subtitle={drill ? `${drill.subtitle} ${label}.` : ''}
        fetchPage={fetchPage}
        describe={describeRecord}
        onClose={() => setDrill(null)}
      />
    </ReportShell>
  );
};

export default HrDailyReport;
